// ===== HWP SDK Messages =====

/** HWP iframe → parent: iframe 로드 완료 (plain string) */
export const HWP_LOAD_COMPLETE = 'hwpctrl-load-complete' as const;

/** Parent → Iframe: HWP 커맨드 요청 */
export interface HwpCommandRequestMessage {
  type: 'HWP_COMMAND';
  requestId: string;
  command: string;
  params?: unknown[];
}

/** Iframe → Parent: HWP 커맨드 응답 */
export interface HwpCommandResponseMessage {
  type: 'HWP_COMMAND_RESPONSE';
  requestId: string;
  success: boolean;
  result?: unknown;
  error?: string;
}

// ===== HCell SDK Messages =====

// -- Parent → Iframe --

export interface HcellPingSdkReadyMessage {
  type: 'PING_SDK_READY';
  timestamp: number;
}

export interface HcellExecuteScriptMessage {
  type: 'EXECUTE_SCRIPT';
  script: string;
}

export interface HcellSdkRefreshMessage {
  type: 'SDK_REFRESH';
  timestamp: number;
}

export interface HcellClearSpreadsheetMessage {
  type: 'CLEAR_SPREADSHEET_CONTENT';
  timestamp: number;
}

export interface HcellRefreshViewerMessage {
  type: 'REFRESH_VIEWER';
}

/** Parent → Iframe 방향 메시지 union */
export type HcellOutboundMessage =
  | HcellPingSdkReadyMessage
  | HcellExecuteScriptMessage
  | HcellSdkRefreshMessage
  | HcellClearSpreadsheetMessage
  | HcellRefreshViewerMessage;

// -- Iframe → Parent --

export interface HcellPongSdkReadyMessage {
  type: 'PONG_SDK_READY';
  timestamp: number;
}

export interface HcellSdkReadyMessage {
  type: 'SDK_READY';
  source?: string;
  timestamp: number;
}

export interface HcellScriptExecutedMessage {
  type: 'SCRIPT_EXECUTED';
  success: boolean;
  message?: string;
  error?: string;
}

export interface HcellImageInsertResultMessage {
  type: 'IMAGE_INSERT_RESULT';
  fileName: string;
  message: string;
}

export interface HcellClearSpreadsheetResponseMessage {
  type: 'CLEAR_SPREADSHEET_CONTENT_RESPONSE';
  success: boolean;
  message?: string;
  error?: string;
}

/** Iframe → Parent 방향 메시지 union */
export type HcellInboundMessage =
  | HcellPongSdkReadyMessage
  | HcellSdkReadyMessage
  | HcellScriptExecutedMessage
  | HcellImageInsertResultMessage
  | HcellClearSpreadsheetResponseMessage;

/** HCell 전체 메시지 union */
export type HcellMessage = HcellOutboundMessage | HcellInboundMessage;

/** HWP 전체 메시지 union (object 형태) */
export type HwpMessage = HwpCommandRequestMessage | HwpCommandResponseMessage;

/** 모든 SDK 메시지 union */
export type SdkMessage = HcellMessage | HwpMessage;

// ===== Type Guards =====

/** object 형태의 메시지인지 확인 */
export const isSdkMessage = (data: unknown): data is SdkMessage =>
  typeof data === 'object' && data !== null && 'type' in data && typeof data.type === 'string';

/** HWP 로드 완료 string 메시지인지 확인 */
export const isHwpLoadComplete = (data: unknown): data is typeof HWP_LOAD_COMPLETE =>
  data === HWP_LOAD_COMPLETE;

/** HCell 메시지인지 확인 */
export const isHcellMessage = (data: unknown): data is HcellMessage => {
  if (!isSdkMessage(data)) return false;
  const hcellTypes = new Set([
    'PING_SDK_READY',
    'EXECUTE_SCRIPT',
    'SDK_REFRESH',
    'CLEAR_SPREADSHEET_CONTENT',
    'REFRESH_VIEWER',
    'PONG_SDK_READY',
    'SDK_READY',
    'SCRIPT_EXECUTED',
    'IMAGE_INSERT_RESULT',
    'CLEAR_SPREADSHEET_CONTENT_RESPONSE',
  ]);
  return hcellTypes.has((data as { type: string }).type);
};

/** 특정 메시지 타입 확인 */
export const isMessageType = <T extends SdkMessage['type']>(
  data: unknown,
  type: T,
): data is Extract<SdkMessage, { type: T }> =>
  isSdkMessage(data) && (data as { type: string }).type === type;
